// ---------------------------------------------------------------------------
// Legacy-screen read-back — the deterministic half of S3 in
// docs/studio/wonbanking-studio-scenarios-2026-08-08.md.
//
// A conversion does not start from an idea; it starts from a working legacy
// screen, usually JSP with scriptlets mixed into the markup, or Spring/JSTL
// taglibs. The element composition — form fields, tables, buttons — is
// decidable from the paste. The dangerous question is NOT decidable: what to
// carry as-is versus improve. The developer wants "the same screen", but not
// its defects carried along, and resolving that silently in either direction
// is how conversions go wrong. So this module reads the composition without a
// model in the loop, detects a CLOSED set of carry-vs-improve traits (each
// backed by counts, never inferred), and `formatLegacyConversionDecision`
// serializes the human's per-trait ruling for the generation prompt.
//
// Traits deliberately absent from the choice list: scriptlets and inline
// event handlers. Neither can be "kept" — cogo has no scriptlets and events
// become ActionFlow bindings either way — so offering a choice there would be
// a fake control. They are reported as facts (counts) instead, and the
// scriptlet count powers the card's data warning: server logic does not
// travel with the screen (S4 complementarity — the data source must come
// from a contract or a field table).
//
// Conservative like its S1/S2 siblings: prose, JSON, fenced blocks, and
// field tables return null and the creation router treats them as before.
// ---------------------------------------------------------------------------

export type LegacyTraitKey =
  | 'table-layout'
  | 'alert-confirm'
  | 'popup-window'
  | 'deprecated-markup'

export interface LegacyScreenField {
  name: string
  /** input type (text/hidden/…), or the control tag (select/textarea/input). */
  control: string
}

export interface LegacyScreenTable {
  /** <th> texts; empty for header-less (usually layout) tables. */
  columns: string[]
}

export interface LegacyScreenReadback {
  source: 'legacy-screen'
  /** <% … %> blocks, directives included — server logic that will NOT travel. */
  scriptletCount: number
  /** Opening JSTL/Spring taglib tags (<c:…>, <form:…>, <fmt:…>, …). */
  taglibTagCount: number
  formCount: number
  fields: LegacyScreenField[]
  tables: LegacyScreenTable[]
  actions: { label: string }[]
  inlineEventHandlerCount: number
  /** Only traits actually detected, each with its occurrence count. */
  traits: { key: LegacyTraitKey; count: number }[]
}

const SCRIPTLET_PATTERN = /<%[\s\S]*?%>/g
const TAGLIB_PATTERN = /<(?:c|fmt|jsp|spring|form|fn|sec|s|ui):[A-Za-z][\w-]*/g
const INLINE_HANDLER_PATTERN =
  /\son(?:click|change|submit|load|keyup|keydown|keypress|blur|focus|mouseover|mouseout)\s*=/gi
const ALERT_CONFIRM_PATTERN = /\b(?:alert|confirm)\s*\(/g
const POPUP_PATTERN = /window\.open\s*\(/g
const DEPRECATED_PATTERN = /<(?:font|center|marquee|blink)\b|\sbgcolor\s*=/gi
const HTML_CONTROL_PATTERN = /<(?:form|table|input|select|textarea|button)\b/gi

function count(text: string, pattern: RegExp): number {
  return (text.match(pattern) ?? []).length
}

export function parseLegacyScreenReadback(
  text: string,
): LegacyScreenReadback | null {
  const trimmed = text.trim()
  // JSON belongs to S1, fenced blocks are quoted code, and a paste without
  // markup is prose or an S2 field table — none of them are a screen source.
  if (trimmed.startsWith('{') || trimmed.startsWith('```')) return null
  if (!trimmed.includes('<')) return null

  const scriptletCount = count(trimmed, SCRIPTLET_PATTERN)
  const taglibTagCount = count(trimmed, TAGLIB_PATTERN)
  const htmlControlCount = count(trimmed, HTML_CONTROL_PATTERN)

  // A screen source, not a stray tag in prose: either server-side markers are
  // present, or there is enough real markup structure to read.
  if (scriptletCount === 0 && taglibTagCount === 0 && htmlControlCount < 3) {
    return null
  }
  if (typeof DOMParser === 'undefined') return null

  // Scriptlets are not markup; strip them (counted above) so the HTML parser
  // reads the screen the way a browser ultimately did.
  const markup = trimmed.replace(SCRIPTLET_PATTERN, '')
  const documentRoot = new DOMParser().parseFromString(markup, 'text/html')

  const fields: LegacyScreenField[] = []
  const actions: { label: string }[] = []
  const tables: LegacyScreenTable[] = []
  let formCount = 0
  let layoutTableCount = 0

  for (const element of Array.from(documentRoot.getElementsByTagName('*'))) {
    const tag = element.tagName.toLowerCase()
    if (tag === 'form') formCount += 1
    if (tag === 'input') {
      const type = (element.getAttribute('type') ?? 'text').toLowerCase()
      const name = element.getAttribute('name')
      if (['submit', 'button', 'image'].includes(type)) {
        const label = element.getAttribute('value')?.trim()
        if (label) actions.push({ label })
      } else if (name) {
        fields.push({ name, control: type })
      }
      continue
    }
    if (tag === 'select' || tag === 'textarea') {
      const name = element.getAttribute('name')
      if (name) fields.push({ name, control: tag })
      continue
    }
    if (tag === 'button') {
      const label = element.textContent?.trim()
      if (label) actions.push({ label })
      continue
    }
    if (tag === 'a') {
      const href = element.getAttribute('href') ?? ''
      const label = element.textContent?.trim()
      if (href.startsWith('javascript:') && label) actions.push({ label })
      continue
    }
    if (tag === 'table') {
      const headers = Array.from(element.getElementsByTagName('th'))
        .map((th) => th.textContent?.trim() ?? '')
        .filter(Boolean)
      tables.push({ columns: headers })
      if (
        headers.length === 0 &&
        element.querySelector('input, select, textarea') !== null
      ) {
        layoutTableCount += 1
      }
      continue
    }
    // Spring form taglib controls carry the binding in `path`.
    const colon = tag.indexOf(':')
    if (colon !== -1) {
      const path = element.getAttribute('path')
      if (path) fields.push({ name: path, control: tag.slice(colon + 1) })
    }
  }

  const traits: { key: LegacyTraitKey; count: number }[] = []
  if (layoutTableCount > 0) {
    traits.push({ key: 'table-layout', count: layoutTableCount })
  }
  const alertConfirm = count(trimmed, ALERT_CONFIRM_PATTERN)
  if (alertConfirm > 0) traits.push({ key: 'alert-confirm', count: alertConfirm })
  const popups = count(trimmed, POPUP_PATTERN)
  if (popups > 0) traits.push({ key: 'popup-window', count: popups })
  const deprecated = count(trimmed, DEPRECATED_PATTERN)
  if (deprecated > 0) {
    traits.push({ key: 'deprecated-markup', count: deprecated })
  }

  return {
    source: 'legacy-screen',
    scriptletCount,
    taglibTagCount,
    formCount,
    fields,
    tables,
    actions,
    inlineEventHandlerCount: count(trimmed, INLINE_HANDLER_PATTERN),
    traits,
  }
}

/**
 * Serializes the human's per-trait ruling. Stable, language-independent keys;
 * every DETECTED trait appears exactly once, under `keep` unless the human
 * chose otherwise — faithful conversion is the baseline, improvement is the
 * deliberate act, and an all-keep pass is still recorded because deciding is
 * the point.
 */
export function formatLegacyConversionDecision(
  readback: LegacyScreenReadback,
  rulings: Partial<Record<LegacyTraitKey, 'keep' | 'improve'>>,
): string {
  const keep: LegacyTraitKey[] = []
  const improve: LegacyTraitKey[] = []
  for (const trait of readback.traits) {
    if (rulings[trait.key] === 'improve') improve.push(trait.key)
    else keep.push(trait.key)
  }

  const lines = ['[legacy-conversion-decision]']
  if (keep.length > 0) lines.push(`keep: ${keep.join(', ')}`)
  if (improve.length > 0) lines.push(`improve: ${improve.join(', ')}`)
  return lines.join('\n')
}
